import type { ValidationRejection } from "@altostra/type-validations";
import { arrayOf, objectOf } from "@altostra/type-validations";
import { asRejectingValidator } from "@altostra/type-validations/lib/RejectionReasons";
import { number, string } from "@altostra/type-validations/lib/primitives";

import type { GetDecisioningStudioAgentsAppGroupIdDsaIdAgentPreferencesResult } from "./types";

const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;

const isIsoDate = asRejectingValidator(
  (value: unknown): value is string => typeof value === "string" && isoDateRegex.test(value),
  "ISODate"
);

const isAgentPreferencesResultShape = objectOf({
  table_labels: arrayOf(string),
  table_values: arrayOf(number),
  chart_labels: arrayOf(string),
  chart_values: arrayOf(arrayOf(number)),
  chart_dates: arrayOf(isIsoDate),
});

export const isGetDecisioningStudioAgentsAppGroupIdDsaIdAgentPreferencesResult = (
  value: unknown,
  rejectionReasons?: (rejection: ValidationRejection) => void
): value is GetDecisioningStudioAgentsAppGroupIdDsaIdAgentPreferencesResult => {
  if (!isAgentPreferencesResultShape(value, rejectionReasons)) {
    return false;
  }

  const response = value as GetDecisioningStudioAgentsAppGroupIdDsaIdAgentPreferencesResult;
  if (response.table_labels.length !== response.table_values.length) {
    rejectionReasons?.({
      path: ["table_labels", "table_values"],
      reason: "Table labels and values must have matching lengths",
      propertyType: "aligned arrays",
    });
    return false;
  }
  if (response.chart_labels.length !== response.chart_values.length) {
    rejectionReasons?.({
      path: ["chart_labels", "chart_values"],
      reason: "Chart labels and values must have matching lengths",
      propertyType: "aligned arrays",
    });
    return false;
  }

  const chartValuesAreAligned = response.chart_values.every((row, rowIndex) => {
    if (row.length === response.chart_dates.length) {
      return true;
    }

    rejectionReasons?.({
      path: ["chart_values", rowIndex],
      reason: "Chart values must have one value for each chart date",
      propertyType: "aligned arrays",
    });
    return false;
  });

  return chartValuesAreAligned;
};
